<template>
    <div class="tech-chart-card">
        <div class="tech-chart-card__header">
            <div>
                <p class="tech-chart-card__eyebrow">{{ eyebrow }}</p>
                <h3 class="tech-chart-card__title">{{ title }}</h3>
            </div>
            <span v-if="total !== null && total !== undefined" class="tech-chart-card__badge">
                {{ total }} total
            </span>
        </div>
        <div
            v-if="hasData"
            class="tech-chart-card__body tech-chart-card__body--center"
            :class="{ 'tech-chart-card__body--interactive': isSelectable }"
        >
            <apexchart
                type="donut"
                height="280"
                :options="chartOptions"
                :series="series"
                @dataPointSelection="onSliceSelect"
                @legendClick="onLegendClick"
            ></apexchart>
        </div>
        <div v-else class="tech-chart-card__empty">No data available.</div>
    </div>
</template>

<script>
export default {
    name: "TechDonutChart",
    emits: ["select"],
    props: {
        title: { type: String, default: "Breakdown" },
        eyebrow: { type: String, default: "Distribution" },
        labels: {
            type: Array,
            default: function () {
                return [];
            },
        },
        values: {
            type: Array,
            default: function () {
                return [];
            },
        },
        colors: {
            type: Array,
            default: function () {
                return ["#0f766e", "#134e4a", "#0891b2", "#64748b"];
            },
        },
        moduleKeys: {
            type: Array,
            default: function () {
                return [];
            },
        },
        total: { type: Number, default: null },
    },
    computed: {
        isSelectable: function () {
            return Array.isArray(this.moduleKeys) && this.moduleKeys.length > 0;
        },
        hasData: function () {
            return Array.isArray(this.values) && this.values.some(function (v) {
                return Number(v) > 0;
            });
        },
        series: function () {
            return Array.isArray(this.values)
                ? this.values.map(function (v) {
                      return Number(v) || 0;
                  })
                : [];
        },
        chartOptions: function () {
            var labels = Array.isArray(this.labels) ? this.labels : [];
            var colors = Array.isArray(this.colors) ? this.colors : [];
            var selectable = this.isSelectable;
            return {
                chart: {
                    type: "donut",
                    fontFamily: "inherit",
                    animations: {
                        enabled: true,
                        easing: "easeinout",
                        speed: 650,
                    },
                },
                labels: labels,
                colors: colors,
                legend: {
                    position: "bottom",
                    fontSize: "12px",
                    fontWeight: 600,
                    markers: {
                        width: 10,
                        height: 10,
                        radius: 10,
                    },
                    formatter: function (val, opts) {
                        var value = opts.w.globals.series[opts.seriesIndex];
                        return val + " · " + value;
                    },
                    onItemClick: {
                        toggleDataSeries: !selectable,
                    },
                },
                dataLabels: { enabled: false },
                stroke: {
                    width: 3,
                    colors: ["#ffffff"],
                },
                plotOptions: {
                    pie: {
                        donut: {
                            size: "68%",
                            labels: {
                                show: true,
                                name: {
                                    show: true,
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: "#64748b",
                                    offsetY: -6,
                                },
                                value: {
                                    show: true,
                                    fontSize: "24px",
                                    fontWeight: 700,
                                    color: "#0f172a",
                                    offsetY: 6,
                                    formatter: function (val) {
                                        return val;
                                    },
                                },
                                total: {
                                    show: true,
                                    label: "Total",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: "#64748b",
                                    formatter: function (w) {
                                        var sum = 0;
                                        var series = w.globals.seriesTotals;
                                        for (var i = 0; i < series.length; i++) {
                                            sum += series[i];
                                        }
                                        return sum;
                                    },
                                },
                            },
                        },
                        expandOnClick: selectable,
                    },
                },
                tooltip: {
                    theme: "light",
                    y: {
                        formatter: function (val) {
                            return val + " items";
                        },
                    },
                },
                states: {
                    hover: {
                        filter: { type: "lighten", value: 0.08 },
                    },
                    active: {
                        allowMultipleDataPointsSelection: false,
                        filter: { type: "darken", value: 0.12 },
                    },
                },
                responsive: [
                    {
                        breakpoint: 640,
                        options: {
                            chart: { height: 260 },
                            legend: { position: "bottom" },
                        },
                    },
                ],
            };
        },
    },
    methods: {
        emitAtIndex: function (index) {
            if (!this.isSelectable) {
                return;
            }
            var labels = Array.isArray(this.labels) ? this.labels : [];
            var keys = Array.isArray(this.moduleKeys) ? this.moduleKeys : [];
            if (index < 0 || index >= keys.length) {
                return;
            }

            var key = keys[index] || null;
            if (!key) {
                return;
            }

            this.$emit("select", {
                index: index,
                category: labels[index] || "",
                key: key,
                count: Array.isArray(this.values) ? Number(this.values[index]) || 0 : 0,
            });
        },
        onSliceSelect: function (event, chartContext, config) {
            var index = -1;
            if (config && typeof config.dataPointIndex === "number" && config.dataPointIndex >= 0) {
                index = config.dataPointIndex;
            } else if (config && typeof config.seriesIndex === "number" && config.seriesIndex >= 0) {
                index = config.seriesIndex;
            }
            this.emitAtIndex(index);
        },
        onLegendClick: function (chartContext, seriesIndex) {
            this.emitAtIndex(typeof seriesIndex === "number" ? seriesIndex : -1);
        },
    },
};
</script>

<style lang="scss" scoped>
@import "./chart-card.scss";

.tech-chart-card__body--interactive :deep(.apexcharts-pie-area),
.tech-chart-card__body--interactive :deep(.apexcharts-legend-series) {
    cursor: pointer;
}
</style>
